import * as React from 'react';
import { CalendarDays, Check } from 'lucide-react';
import { cn } from '../../lib/cn';
import { useRovingRadioGroup } from '../../hooks/use-roving-radio-group';
import { formatAtlasDate } from '../../lib/atlas-date';
import { Button } from '../button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { MultiSelect, type MultiSelectOption } from './MultiSelect';

/**
 * Asia/Dubai, fixed offset (no DST) — the same anchor the sales rollups use
 * (`sales_metrics_daily` is keyed off a snapshot date computed once a day;
 * every report tab reads that as a Dubai calendar day). Every other
 * Dubai-anchored preset control in this codebase (`onhire-chart.tsx`,
 * `combined-onhire-page.tsx`, `marketplace-fleet-hub-page.tsx`,
 * `TimePeriodSelector.tsx`) reaches the same "today" the same way — a preset
 * picker here that used the VIEWER's local day instead would ask an agent in
 * Lahore or London for one day and the RPC for another (Hassan, 2026-09-30).
 */
export const DUBAI_TIME_ZONE = 'Asia/Dubai';

export type ReportPeriodPreset =
  | 'today'
  | 'yesterday'
  | 'two_days_ago'
  | 'three_days_ago'
  | 'this_week'
  | 'last_week'
  | 'last_7d'
  | 'last_8d'
  | 'last_15d'
  | 'last_30d'
  | 'mtd'
  | 'qtd'
  | 'ytd'
  | 'last_90d'
  | 'custom';

export interface ReportFiltersValue {
  period: ReportPeriodPreset;
  /** Inclusive start date (ISO 8601, `YYYY-MM-DD`). */
  from: string;
  /** Inclusive end date (ISO 8601, `YYYY-MM-DD`). */
  to: string;
  owners: string[];
  productTracks: string[];
  sources: string[];
}

export interface ReportFiltersProps {
  value: ReportFiltersValue;
  onChange: (next: ReportFiltersValue) => void;
  ownerOptions: MultiSelectOption[];
  /** Hide when the report does not support an owner cut. */
  showOwnerFilter?: boolean;
  /**
   * Omit (or pass an empty array) to hide the control entirely. A filter
   * the caller's query cannot honour must not be rendered — see B56: the
   * sales reports shipped a product-track picker that no report RPC ever
   * read, so selecting "Flex" returned unfiltered numbers that looked
   * filtered.
   */
  productTrackOptions?: MultiSelectOption[];
  /** Same contract as productTrackOptions. */
  sourceOptions?: MultiSelectOption[];
  /**
   * Which presets to render as radio buttons, in order. Omit to keep the
   * full default list (every existing call site) — a report that only
   * supports a subset (Lead origins v2: Today/Yesterday/2–3 days ago/
   * Last 7/15/30 days/Custom, Hassan 2026-10-02) passes its own list
   * rather than the caller silently tolerating a preset its RPCs do not
   * expect. Custom's input row below is unaffected either way.
   */
  presets?: ReportPeriodPreset[];
  className?: string;
}

// Order is the product spec (Hassan, 2026-09-30): short single days first
// (most common triage asks — "how did yesterday go"), then the two weekly
// windows, then the named accounting periods, widest last. last_7d/15d/30d
// (Hassan, 2026-10-02, Lead origins v2) sit right after the weekly windows —
// the same "widening rolling window" family as last_8d/last_90d, which they
// deliberately do not replace: last_8d already has callers.
const PRESET_KEYS: ReportPeriodPreset[] = [
  'today',
  'yesterday',
  'two_days_ago',
  'three_days_ago',
  'this_week',
  'last_week',
  'last_7d',
  'last_8d',
  'last_15d',
  'last_30d',
  'mtd',
  'qtd',
  'ytd',
  'last_90d',
  'custom',
];

const PRESET_LABELS: Record<ReportPeriodPreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  two_days_ago: '2 days ago',
  three_days_ago: '3 days ago',
  this_week: 'This week',
  last_week: 'Last week',
  last_7d: 'Last 7 days',
  last_8d: 'Last 8 days',
  last_15d: 'Last 15 days',
  last_30d: 'Last 30 days',
  mtd: 'MTD',
  qtd: 'QTD',
  ytd: 'YTD',
  last_90d: 'Last 90 days',
  custom: 'Custom',
};

const DUBAI_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: DUBAI_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * All date math below works in whole calendar days, never in real instants:
 * every `Date` this module produces is a UTC-MIDNIGHT stand-in for a
 * calendar day and is read back only through `getUTC*`. That is deliberate,
 * not incidental — `date-fns`'s plain `startOfMonth`/`subDays`/etc. read the
 * JS runtime's OWN local timezone (`getFullYear`/`getMonth`/`getDate`), so
 * the previous implementation (`todayUTC()` + those functions) silently
 * depended on the browser or test runner already being set to UTC. Doing
 * the arithmetic by hand on `getUTC*`/`Date.UTC` throughout means the
 * result is identical regardless of the viewer's or CI's own clock —
 * provably so, since every function here takes `now` as a parameter.
 */
function dubaiCalendarParts(now: Date): { y: number; m: number; d: number } {
  const parts = DUBAI_DAY_FORMATTER.formatToParts(now);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

/** Today's Asia/Dubai calendar date, as a UTC-midnight day-token. */
export function dubaiToday(now: Date = new Date()): Date {
  const { y, m, d } = dubaiCalendarParts(now);
  return new Date(Date.UTC(y, m - 1, d));
}

function addUtcDays(day: Date, delta: number): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + delta));
}

function startOfUtcMonth(day: Date): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), 1));
}

function startOfUtcQuarter(day: Date): Date {
  const quarterMonth = Math.floor(day.getUTCMonth() / 3) * 3;
  return new Date(Date.UTC(day.getUTCFullYear(), quarterMonth, 1));
}

function startOfUtcYear(day: Date): Date {
  return new Date(Date.UTC(day.getUTCFullYear(), 0, 1));
}

/**
 * Monday-start ISO week — matches the week start the previous `This Week`
 * preset used (date-fns `startOfISOWeek`, always Monday regardless of
 * locale). `getUTCDay()` is 0 (Sun) .. 6 (Sat).
 */
function startOfUtcIsoWeek(day: Date): Date {
  const dow = day.getUTCDay();
  const daysSinceMonday = dow === 0 ? 6 : dow - 1;
  return addUtcDays(day, -daysSinceMonday);
}

function toISODate(day: Date): string {
  const y = day.getUTCFullYear();
  const m = String(day.getUTCMonth() + 1).padStart(2, '0');
  const d = String(day.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const ISO_DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A shape-valid but calendar-invalid string (`2026-02-31`) must not silently
 * normalise to March 3 — `Date.UTC` does that normalisation, so round-trip
 * the parsed value and reject any mismatch (same guard `isValidIsoDay` in
 * `DateRangeControl.tsx` uses).
 */
function parseISODay(value: string | undefined | null): Date | null {
  if (!value) return null;
  const match = ISO_DAY_RE.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const day = new Date(Date.UTC(y, m - 1, d));
  return day.getUTCFullYear() === y && day.getUTCMonth() === m - 1 && day.getUTCDate() === d
    ? day
    : null;
}

/** A report range is inclusive and must always run forwards. */
export function normalizeReportDateRange(from: Date, to: Date): { from: Date; to: Date } {
  return from.getTime() > to.getTime() ? { from: to, to: from } : { from, to };
}

export function resolveReportPeriod(
  period: ReportPeriodPreset,
  custom?: { from: Date; to: Date },
  now: Date = new Date(),
): { from: Date; to: Date } {
  const today = dubaiToday(now);
  switch (period) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = addUtcDays(today, -1);
      return { from: day, to: day };
    }
    case 'two_days_ago': {
      const day = addUtcDays(today, -2);
      return { from: day, to: day };
    }
    case 'three_days_ago': {
      const day = addUtcDays(today, -3);
      return { from: day, to: day };
    }
    case 'this_week':
      return { from: startOfUtcIsoWeek(today), to: today };
    case 'last_week': {
      const thisWeekStart = startOfUtcIsoWeek(today);
      return { from: addUtcDays(thisWeekStart, -7), to: addUtcDays(thisWeekStart, -1) };
    }
    case 'last_7d':
      return { from: addUtcDays(today, -6), to: today };
    case 'last_8d':
      return { from: addUtcDays(today, -7), to: today };
    case 'last_15d':
      return { from: addUtcDays(today, -14), to: today };
    case 'last_30d':
      return { from: addUtcDays(today, -29), to: today };
    case 'mtd':
      return { from: startOfUtcMonth(today), to: today };
    case 'qtd':
      return { from: startOfUtcQuarter(today), to: today };
    case 'ytd':
      return { from: startOfUtcYear(today), to: today };
    case 'last_90d':
      return { from: addUtcDays(today, -89), to: today };
    case 'custom':
      return custom
        ? normalizeReportDateRange(custom.from, custom.to)
        : { from: addUtcDays(today, -89), to: today };
  }
}

export function defaultReportFiltersValue(): ReportFiltersValue {
  const { from, to } = resolveReportPeriod('mtd');
  return {
    period: 'mtd',
    from: toISODate(from),
    to: toISODate(to),
    owners: [],
    productTracks: [],
    sources: [],
  };
}

export function reportFiltersFromSearchParams(params: URLSearchParams): ReportFiltersValue {
  const rawPeriod = params.get('period') ?? 'mtd';
  const period = PRESET_KEYS.includes(rawPeriod as ReportPeriodPreset)
    ? (rawPeriod as ReportPeriodPreset)
    : 'mtd';

  const fromDay = parseISODay(params.get('from'));
  const toDay = parseISODay(params.get('to'));
  const { from, to } =
    period === 'custom' && fromDay && toDay
      ? normalizeReportDateRange(fromDay, toDay)
      : resolveReportPeriod(period);

  const parseList = (key: string) =>
    params
      .get(key)
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? [];

  return {
    period,
    from: toISODate(from),
    to: toISODate(to),
    owners: parseList('owners'),
    productTracks: parseList('productTracks'),
    sources: parseList('sources'),
  };
}

export function reportFiltersToSearchParams(
  value: ReportFiltersValue,
  params?: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.set('period', value.period);
  next.set('from', value.from);
  next.set('to', value.to);
  next.set('owners', value.owners.join(','));
  next.set('productTracks', value.productTracks.join(','));
  next.set('sources', value.sources.join(','));
  return next;
}

/**
 * `formatAtlasDate` is the one date-formatting implementation Atlas uses
 * everywhere else (`packages/ui/src/lib/atlas-date.ts`) — deliberately NOT a
 * second `Intl.DateTimeFormat('en-GB', { month: 'short' })` here, because
 * that locale renders September as "Sept" (four letters) while Atlas's own
 * house month strings are fixed at three. `'YYYY-MM-DD'` parses as UTC
 * midnight, and Dubai is UTC+4 — always later the same calendar day, never
 * a day earlier — so formatting it in Asia/Dubai never rolls the date back.
 */
function formatShortDubaiDate(iso: string): string {
  return formatAtlasDate(iso, { omitYear: true });
}

/**
 * The label the period control shows, exact dates included — "Yesterday ·
 * 29 Sep", "MTD · 1 Sep → 30 Sep" — so the number on screen is never a
 * mystery window (Hassan, 2026-09-30: "there is no selection of dates").
 */
export function reportPeriodLabel(value: ReportFiltersValue): string {
  const name = PRESET_LABELS[value.period] ?? 'Custom';
  if (value.from === value.to) return `${name} · ${formatShortDubaiDate(value.from)}`;
  const range = `${formatShortDubaiDate(value.from)} → ${formatShortDubaiDate(value.to)}`;
  return value.period === 'custom' ? range : `${name} · ${range}`;
}

export function ReportFilters({
  value,
  onChange,
  ownerOptions,
  showOwnerFilter = true,
  productTrackOptions,
  sourceOptions,
  presets = PRESET_KEYS,
  className,
}: ReportFiltersProps) {
  const radioGroup = useRovingRadioGroup();
  const fromId = React.useId();
  const toId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState(value.from);
  const [draftTo, setDraftTo] = React.useState(value.to);
  const today = React.useMemo(() => toISODate(dubaiToday()), []);

  // Re-seed the draft from the live value every time the popover opens, so a
  // custom range abandoned mid-edit (closed without Apply) doesn't reappear
  // half-typed the next time the control is opened.
  React.useEffect(() => {
    if (open) {
      setDraftFrom(value.from);
      setDraftTo(value.to);
    }
  }, [open, value.from, value.to]);

  const applyPreset = (preset: Exclude<ReportPeriodPreset, 'custom'>) => {
    const { from, to } = resolveReportPeriod(preset);
    onChange({ ...value, period: preset, from: toISODate(from), to: toISODate(to) });
    setOpen(false);
  };

  const [orderedFrom, orderedTo] =
    draftFrom && draftTo && draftFrom > draftTo ? [draftTo, draftFrom] : [draftFrom, draftTo];
  const customRangeValid =
    Boolean(orderedFrom && orderedTo) && parseISODay(orderedFrom) !== null && parseISODay(orderedTo) !== null;

  const applyCustom = () => {
    if (!customRangeValid) return;
    onChange({ ...value, period: 'custom', from: orderedFrom, to: orderedTo });
    setOpen(false);
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            className="min-h-11 gap-2 tabular-nums"
            aria-label={`Report period: ${reportPeriodLabel(value)}`}
          >
            <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{reportPeriodLabel(value)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2">
          <div role="radiogroup" aria-label="Report period" {...radioGroup} className="flex flex-col gap-0.5">
            {presets.filter((preset): preset is Exclude<ReportPeriodPreset, 'custom'> => preset !== 'custom').map(
              (preset) => {
                const active = value.period === preset;
                return (
                  <button
                    key={preset}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => applyPreset(preset)}
                    className={cn(
                      'flex min-h-11 w-full items-center justify-between rounded px-3 text-caption font-medium transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-0',
                      active
                        ? 'bg-accent-500 text-text-on-brand'
                        : 'text-text-secondary hover:bg-bg-inset hover:text-text-primary',
                    )}
                  >
                    {PRESET_LABELS[preset]}
                    {active ? <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
                  </button>
                );
              },
            )}
          </div>

          <div className="my-2 h-px bg-border" aria-hidden="true" />

          <div role="group" aria-label="Custom report range" className="flex flex-col gap-2 px-1 pb-1">
            <div className="flex items-center gap-2">
              <label htmlFor={fromId} className="w-9 shrink-0 text-caption text-text-muted">
                From
              </label>
              <input
                id={fromId}
                type="date"
                value={draftFrom}
                max={draftTo || today}
                onChange={(e) => setDraftFrom(e.target.value)}
                className="min-h-11 w-full min-w-0 rounded border border-border bg-bg-surface px-2 text-caption text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-0"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor={toId} className="w-9 shrink-0 text-caption text-text-muted">
                To
              </label>
              <input
                id={toId}
                type="date"
                value={draftTo}
                min={draftFrom || undefined}
                onChange={(e) => setDraftTo(e.target.value)}
                className="min-h-11 w-full min-w-0 rounded border border-border bg-bg-surface px-2 text-caption text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-0"
              />
            </div>
            {!customRangeValid && (draftFrom || draftTo) ? (
              <p role="alert" className="text-caption text-destructive">
                Pick a from date on or before the to date.
              </p>
            ) : null}
            <Button
              type="button"
              variant={value.period === 'custom' ? 'primary' : 'secondary'}
              className="min-h-11 w-full"
              disabled={!customRangeValid}
              onClick={applyCustom}
            >
              Apply custom range
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {showOwnerFilter && (
        <MultiSelect
          options={ownerOptions}
          value={value.owners}
          onChange={(owners) => onChange({ ...value, owners })}
          placeholder="Owners"
          searchLabel="Search owners"
          triggerClassName="min-h-11"
        />
      )}

      {productTrackOptions && productTrackOptions.length > 0 && (
        <MultiSelect
          options={productTrackOptions}
          value={value.productTracks}
          onChange={(productTracks) => onChange({ ...value, productTracks })}
          placeholder="Product track"
          searchLabel="Search product tracks"
          triggerClassName="min-h-11"
        />
      )}

      {sourceOptions && sourceOptions.length > 0 && (
        <MultiSelect
          options={sourceOptions}
          value={value.sources}
          onChange={(sources) => onChange({ ...value, sources })}
          placeholder="Sources"
          searchLabel="Search sources"
          triggerClassName="min-h-11"
        />
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="min-h-11"
        onClick={() => onChange(defaultReportFiltersValue())}
      >
        Reset
      </Button>
    </div>
  );
}
